import { Image } from 'expo-image';
import { Link, Redirect } from 'expo-router';
import Head from 'expo-router/head';
import { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { Box, Button, Header, Text, VStack } from '@fhs-legacy/universal-components';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import useEffectOnce from 'hooks/use-effect-once';
import { usePathname } from 'hooks/use-pathname';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

interface IRedirect {
  searchPattern: string[]; // Regex patterns used to track URLs
  to: string; // Final route we should sent the user
}

/**
 * New feature to identify by patterns if a URL non exists in the app
 * need to be redirected to a different section.
 */
const NotFound = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();
  const { formatMessage } = useIntl();
  const message = `Not Found: ${pathname}`;
  useRumPageView('not-found', message);

  useEffectOnce(() => {
    /**
     * In the case of React Native, we navigate the user back to the home page
     * If we encounter an unexpected route which leads us to rendering the NotFound component
     */
    logger.warn(message);
  });

  const redirects: IRedirect[] = useFlag(LaunchDarklyFlag.REDIRECT_OLD_PAGES) || [];

  const redirectSearch = redirects.find(item =>
    item.searchPattern.some(pattern => pathname.match(new RegExp(pattern)))
  );

  if (redirectSearch) {
    return <Redirect href={redirectSearch.to} />;
  }

  const defaultContent = (
    <>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <VStack alignItems="center" py="$40" height="full" justifyContent="center" background="white">
        <Box w="90%" maxW={288}>
          <Image
            source={require('./404.webp')}
            contentFit="contain"
            style={{ aspectRatio: 288 / 100 }}
          />
        </Box>
        <Header textAlign="center" fontWeight="normal" mt="$6">
          {formatMessage({ id: 'pageNotFound' })}
        </Header>
        <Text textAlign="center" maxW={370}>
          {formatMessage({ id: 'pageNotFoundDescription' })}
        </Text>
        <Link asChild href={routes.base}>
          <Button mt="$6">{formatMessage({ id: 'goToHomepage' })}</Button>
        </Link>
      </VStack>
    </>
  );

  return children || defaultContent;
};

export default NotFound;
